/**
 * Basic validation functions
 */

import {
  EMAIL_REGEX,
  PHONE_REGEX,
  ZIP_REGEX,
  NAME_REGEX,
  CITY_REGEX,
  US_STATE_CODES,
  US_STATE_NAMES,
  EIN_REGEX,
} from './constants';

export const validators = {
  email: (email: string): boolean => EMAIL_REGEX.test(email.trim()),

  phone: (phone: string): boolean => PHONE_REGEX.test(phone.replace(/\D/g, '')),

  ein: (ein: string): boolean => EIN_REGEX.test(ein.replace(/\D/g, '')),

  zip: (zip: string): boolean => ZIP_REGEX.test(zip.trim()),

  name: (name: string): boolean => {
    const trimmed = name.trim();
    return trimmed.length > 0 && NAME_REGEX.test(trimmed);
  },

  city: (city: string): boolean => {
    const trimmed = city.trim();
    return trimmed.length > 0 && CITY_REGEX.test(trimmed);
  },

  state: (state: string, format?: 'code' | 'name'): boolean => {
    const trimmed = state.trim();
    const upper = trimmed.toUpperCase();
    const checkFormat = format || 'code';

    // Check 2-letter code
    if (checkFormat === 'code') {
      if (US_STATE_CODES.has(upper)) return true;
    }

    // Check full state name (case-insensitive)
    if (checkFormat === 'name') {
      if (
        US_STATE_NAMES.has(trimmed) ||
        Array.from(US_STATE_NAMES).some((name) => name.toLowerCase() === trimmed.toLowerCase())
      ) {
        return true;
      }
    }

    return false;
  },

  password: (password: string): boolean => password.length >= 8,

  passwordMatch: (password: string, confirm: string): boolean => password === confirm,

  verificationCode: (code: string): boolean => {
    const digitsOnly = code.replace(/\D/g, '');
    return digitsOnly.length === 6;
  },

  required: (value: unknown): boolean => {
    if (typeof value === 'string') return value.trim().length > 0;
    return value !== null && value !== undefined;
  },

  age18: (dateString: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString + 'T00:00:00');
    if (isNaN(date.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
      age--;
    }

    return age >= 18;
  },

  // Additional validators for pool forms
  propertyValue: (value: string): boolean => {
    if (!value || value.trim() === '') return false; // Required field
    // Remove $, commas, spaces, and + signs
    const cleaned = value.replace(/[$,\s+]/g, '');
    // Check if it contains only numbers and at most one decimal point
    if (!/^\d*\.?\d*$/.test(cleaned)) return false;
    const num = parseFloat(cleaned);
    return !isNaN(num) && num > 0 && isFinite(num);
  },

  coOwnerPercentage: (percentage: string): boolean => {
    if (!percentage || percentage.trim() === '') return false;
    const num = parseFloat(percentage);
    return !isNaN(num) && num > 0 && num <= 100;
  },

  propertyLink: (link: string): boolean => {
    if (!link || link.trim() === '') return false; // Required field
    try {
      new URL(link);
      return true;
    } catch {
      return false;
    }
  },

  loanAmount: (amount: string): boolean => {
    if (!amount || amount.trim() === '') return false;
    const cleaned = amount.replace(/[$,\s]/g, '');
    const num = parseFloat(cleaned);
    return !isNaN(num) && num > 0;
  },

  poolAmount: (amount: string): boolean => {
    if (!amount || amount.trim() === '') return false;
    const cleaned = amount.replace(/[,$\s]/g, '');
    const num = parseFloat(cleaned);
    return !isNaN(num) && num > 0 && num <= 10000000000; // Max $10B
  },

  roiRate: (rate: string): boolean => {
    if (!rate || rate.trim() === '') return false;
    const cleaned = rate.replace(/[%\s]/g, '');
    const num = parseFloat(cleaned);
    return !isNaN(num) && num > 0 && num <= 100; // Max 100%
  },

  term: (term: string): boolean => {
    if (!term || term.trim() === '') return false;
    const num = parseFloat(term);
    return !isNaN(num) && num > 0 && num <= 360; // Max 30 years (360 months)
  },
};
